// Maps a passkey ceremony failure to a localized message id so the notification
// body is translated (owners see Japanese/English per locale) instead of the raw
// English error. Two error sources:
//   - Browser (@simplewebauthn/browser WebAuthnError): stable `code`, flat `message`.
//   - owner-api (HttpErrors), reaching here via ApiClient/ceremony rejection: the
//     raw LoopBack error envelope, `{ error: { message, statusCode, ... } }` (see
//     utils/errors.js's getMessage(), which unwraps this same shape) - the message
//     lives one level deeper than the browser case, not at the top level.
// Anything unrecognised falls back to a generic message. Every id below must
// exist in the live translations (en + ja) - there is no inline default.
const passkeyErrorMessageId = error => {
  const code = error?.code;
  const message = error?.message ?? error?.error?.message ?? '';

  // Browser errors, matched by code.
  // NotAllowedError - the ceremony timed out or the owner cancelled it. This is
  // the case whose raw browser message carries the w3.org spec link.
  if (code === 'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY') {
    return 'page.passkey.error.cancelled';
  }
  if (code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED') {
    return 'page.passkey.error.alreadyRegistered';
  }

  // owner-api errors, matched by message text.
  // The single-use challenge lapsed (5-min TTL) or was replayed - a fresh page
  // load issues a new one, so the message tells the owner to refresh.
  if (/challenge is invalid or expired/i.test(message)) {
    return 'page.passkey.error.challengeExpired';
  }
  if (/already registered/i.test(message)) {
    return 'page.passkey.error.alreadyRegistered';
  }
  if (/maximum of .* passkeys reached/i.test(message)) {
    return 'page.passkey.error.limitReached';
  }
  if (/signing in with an existing passkey/i.test(message)) {
    return 'page.passkey.error.sessionRequired';
  }
  if (/too many attempts/i.test(message)) {
    return 'page.passkey.error.rateLimited';
  }

  return 'page.passkey.error.generic';
};

export default passkeyErrorMessageId;
